import React from 'react';
import { StarIcon, FlameIcon, MoonIcon } from 'lucide-react';
import { formatIcons } from './constants';
interface BookHoverCardProps {
  book: any;
  darkMode: boolean;
  mousePosition: {
    x: number;
    y: number;
  };
}
// Enhanced BookHover Card component
export const BookHoverCard: React.FC<BookHoverCardProps> = ({
  book,
  darkMode,
  mousePosition
}) => {
  const showOnLeft = mousePosition.x > window.innerWidth / 2;
  const cardWidth = 280;
  const spiceLevel = book.spiceLevel ?? 0;
  const darknessLevel = book.darknessLevel ?? 0;
  return <div className={`fixed z-30 w-72 p-4 rounded-lg shadow-md border ${darkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-300'}`} style={{
    top: Math.max(mousePosition.y - 100, 20),
    left: showOnLeft ? Math.max(mousePosition.x - cardWidth - 20, 20) : Math.min(mousePosition.x + 20, window.innerWidth - cardWidth - 20)
  }} data-id="element-10759" data-mp="2dxoj07ju6awc">
      <div className="flex gap-3" data-id="element-10760" data-mp="jo8y1l026f0c">
        <div className="w-20 h-28 flex-shrink-0 flex flex-col" data-id="element-10761" data-mp="jp0tmvtnocqm">
          <div className="w-full h-full rounded bg-center bg-cover" style={{
          backgroundImage: `url(${book.coverImage})`
        }} data-id="element-10762" data-mp="onc51azlmvay" />
          {book.status === 'read' && <div className="mt-1 flex items-center justify-center bg-black bg-opacity-70 rounded-full p-1 text-white text-xs" data-id="element-10763" data-mp="ydevb3fc5v5l">
              <StarIcon className="h-3 w-3 text-amber-500 mr-0.5 fill-current" data-id="element-10764" data-mp="qxzk2m93ply" />
              <span data-id="element-10765" data-mp="5pj1q7l2cjd1">4.5</span>
            </div>}
        </div>
        <div className="flex-1 overflow-hidden" data-id="element-10766" data-mp="anu98bnvyhs3">
          <h3 className="font-bold text-base mb-1 line-clamp-2" data-id="element-10767" data-mp="alzq92w6czqn">
            {book.title}
          </h3>
          {book.series && <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-10768" data-mp="1m2q0cx3pybpj">
              {book.series} #{book.seriesNumber}
            </p>}
          <p className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-10769" data-mp="1r3nhd6czszpg">
            by {book.author}
          </p>
          <div className="flex flex-wrap gap-1 mt-2" data-id="element-10770" data-mp="3i52w71x8eu9a">
            {book.genre && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-10771" data-mp="3w4m1zo2kltif">
                {book.genre}
              </span>}
            {book.subgenre && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'}`} data-id="element-10772" data-mp="2zfmsy6u5wnp6">
                {book.subgenre}
              </span>}
          </div>
          {book.relationshipType && book.relationshipType !== 'None' && <div className="mt-2" data-id="element-10773" data-mp="2kpf85kgomslb">
              <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-pink-800/60 text-white' : 'bg-pink-100 text-pink-800'}`} data-id="element-10774" data-mp="2kodud88a7rzw">
                {book.relationshipType}
              </span>
            </div>}
          {/* Spice level */}
          {spiceLevel > 0 && <div className="flex items-center gap-1 mt-2" data-id="element-10775" data-mp="1wmsfuzxf4eav">
              <span className={`text-xs w-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10776" data-mp="1wobvqpf5wnc4">
                Spice:
              </span>
              <div className="flex gap-0.5" data-id="element-10777" data-mp="2pb69z4ldyqh5">
                {Array.from({
              length: 5
            }).map((_, i) => <FlameIcon key={i} className={`h-3 w-3 ${i < spiceLevel ? 'text-red-400 fill-red-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-10778" data-mp="2u8gapge86oka" data-mpi={i} />)}
              </div>
            </div>}
          {/* Darkness level */}
          {darknessLevel > 0 && <div className="flex items-center gap-1 mt-1" data-id="element-10779" data-mp="18xg7g29x0a9m">
              <span className={`text-xs w-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10780" data-mp="18utol6hkq8c3">
                Dark:
              </span>
              <div className="flex gap-0.5" data-id="element-10781" data-mp="1kc1d8cpugzhq">
                {Array.from({
              length: 5
            }).map((_, i) => <MoonIcon key={i} className={`h-3 w-3 ${i < darknessLevel ? 'text-indigo-400 fill-indigo-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-10782" data-mp="1afg8lshqlqef" data-mpi={i} />)}
              </div>
            </div>}
          {book.endingType && <div className="mt-2" data-id="element-10783" data-mp="1y4hhfguhf3k8">
              <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-green-800/60 text-white' : 'bg-green-100 text-green-800'}`} data-id="element-10784" data-mp="1y3fxemvhn6qd">
                {book.endingType}
              </span>
            </div>}
          {book.triggerWarnings && book.triggerWarnings.length > 0 && <div className="mt-2" data-id="element-10785" data-mp="ht3syglnedkh">
              <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-red-900 text-red-100' : 'bg-red-100 text-red-800'}`} data-id="element-10786" data-mp="2tevwwxj4mg6e">
                TW: {book.triggerWarnings.join(', ')}
              </span>
            </div>}
          <div className="flex items-center mt-2" data-id="element-10787" data-mp="3830j4bhe19tx">
            <span className={`text-xs flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10788" data-mp="383sdxk35v3j5">
              {formatIcons[book.format]}
              <span className="ml-1" data-id="element-10789" data-mp="3h2xaikv5wmso">{book.format}</span>
            </span>
          </div>
        </div>
      </div>
    </div>;
};